import { FreshServiceProposalWashAlternatives, PriceUtils } from "@repo/common"
import { MessageSquareText, ShowerHead, Star } from "lucide-react"
import { useTranslations } from "use-intl"
import { Badge } from "../../components/ui/badge"
import { Body } from "../../components/ui/typography"
import { useFieldContext } from "../hooks/form-contexts"
import {
  SelectableCard,
  SelectableCardContent,
  SelectableCardDescription,
  SelectableCardHeader,
  SelectableCardIcon,
  SelectableCardIndicator,
  SelectableCardPrice,
  SelectableCardTitle,
} from "./selectable-card"

export interface ServiceProposalWashAlternativesFieldProps {
  washAlternatives: FreshServiceProposalWashAlternatives
}

export const ServiceProposalWashAlternativesField = ({
  washAlternatives,
}: ServiceProposalWashAlternativesFieldProps) => {
  const t = useTranslations("ui")
  const field = useFieldContext<string | undefined>()

  const hasMultipleAlternatives = washAlternatives.alternatives.length > 1
  const isSingleMandatory =
    !hasMultipleAlternatives && washAlternatives.isMandatory

  // Build header text based on mandatory status
  const headerText = washAlternatives.isMandatory
    ? t("proposalForm.washAlternativesMandatory")
    : t("proposalForm.washAlternativesOptional")

  const renderMechanicComment = () =>
    washAlternatives.comment ? (
      <div className="flex items-start gap-2">
        <MessageSquareText className="size-4 text-foreground flex-shrink-0 mt-0.5" />
        <Body size="s" className="text-foreground">
          {washAlternatives.comment}
        </Body>
      </div>
    ) : null

  // For single mandatory wash, render as informational (like fees)
  if (isSingleMandatory) {
    const wash = washAlternatives.alternatives[0]
    if (!wash) return null

    return (
      <SelectableCard selected={false} interactive={false} color="secondary">
        <SelectableCardHeader className="justify-between">
          <div className="flex items-center gap-2">
            <SelectableCardTitle>
              {t("proposalForm.mandatoryWashName", { name: wash.name })}
            </SelectableCardTitle>
            <SelectableCardIcon>
              <ShowerHead />
            </SelectableCardIcon>
          </div>
          <SelectableCardDescription>
            {PriceUtils.format(PriceUtils.getWashPrice(wash))}
          </SelectableCardDescription>
        </SelectableCardHeader>
        {(wash.description || washAlternatives.comment) && (
          <SelectableCardContent>
            {wash.description && (
              <SelectableCardDescription>
                {wash.description}
              </SelectableCardDescription>
            )}
            {renderMechanicComment()}
          </SelectableCardContent>
        )}
      </SelectableCard>
    )
  }

  const renderWashCards = () => (
    <div className="space-y-2">
      {washAlternatives.alternatives.map((wash) => {
        const isSelected = field.state.value === wash.originalWashId

        const handleSelect = () => {
          // If mandatory and already selected, don't deselect
          if (washAlternatives.isMandatory && isSelected) return
          // Use empty string instead of undefined to avoid TanStack Form resetting to defaultValue
          const newValue = isSelected ? "" : wash.originalWashId
          field.handleChange(newValue)
        }

        const shouldDimContent = !isSelected

        return (
          <SelectableCard
            key={wash.originalWashId}
            selected={isSelected}
            onSelect={handleSelect}
            color="secondary"
            variant={washAlternatives.isMandatory ? "radio" : "checkbox"}
          >
            <SelectableCardHeader>
              <div className="flex items-center gap-2">
                <SelectableCardTitle>{wash.name}</SelectableCardTitle>
                <SelectableCardIcon>
                  <ShowerHead />
                </SelectableCardIcon>
                {wash.isRecommended && hasMultipleAlternatives && (
                  <Badge variant="secondary" className="gap-1">
                    <Star className="size-3 fill-current" />
                    {t("readOnlyServiceProposal.recommended")}
                  </Badge>
                )}
              </div>
              <SelectableCardIndicator />
            </SelectableCardHeader>
            <SelectableCardContent>
              <SelectableCardPrice
                className={shouldDimContent ? "opacity-40" : ""}
              >
                {PriceUtils.format(PriceUtils.getWashPrice(wash))}
              </SelectableCardPrice>
              {wash.description && (
                <SelectableCardDescription
                  className={shouldDimContent ? "opacity-40" : ""}
                >
                  {wash.description}
                </SelectableCardDescription>
              )}
            </SelectableCardContent>
          </SelectableCard>
        )
      })}
    </div>
  )

  // If only one alternative (non-mandatory), render without the dashed container
  if (!hasMultipleAlternatives) {
    const wash = washAlternatives.alternatives[0]
    if (!wash) return null

    const isSelected = field.state.value === wash.originalWashId
    const handleSelect = () => {
      const newValue = isSelected ? "" : wash.originalWashId
      field.handleChange(newValue)
    }
    const shouldDimContent = !isSelected

    return (
      <SelectableCard
        selected={isSelected}
        onSelect={handleSelect}
        color="secondary"
        variant="checkbox"
      >
        <SelectableCardHeader>
          <div className="flex items-center gap-2">
            <SelectableCardTitle>{wash.name}</SelectableCardTitle>
            <SelectableCardIcon>
              <ShowerHead />
            </SelectableCardIcon>
          </div>
          <SelectableCardIndicator />
        </SelectableCardHeader>
        <SelectableCardContent>
          <SelectableCardPrice className={shouldDimContent ? "opacity-40" : ""}>
            {PriceUtils.format(PriceUtils.getWashPrice(wash))}
          </SelectableCardPrice>
          {wash.description && (
            <SelectableCardDescription
              className={shouldDimContent ? "opacity-40" : ""}
            >
              {wash.description}
            </SelectableCardDescription>
          )}
          {renderMechanicComment()}
        </SelectableCardContent>
      </SelectableCard>
    )
  }

  return (
    <div className="rounded-lg border-2 border-dashed border-muted-foreground/30 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <ShowerHead className="size-4 text-muted-foreground" />
        <Body size="m" weight="semibold" className="text-muted-foreground">
          {headerText}
        </Body>
      </div>

      {renderMechanicComment()}

      {renderWashCards()}

      {washAlternatives.isMandatory && !field.state.value && (
        <Body size="s" className="text-destructive">
          {t("proposalForm.washRequired")}
        </Body>
      )}
    </div>
  )
}
